/**
 * ExpertFeedbackPanel — Main panel for active expert feedback ("Aider le professeur").
 *
 * Replaces CoachChat when feedback mode is active. Combines:
 * - ML suggestion display (card, confidence, entropy)
 * - CardRanker (drag & drop ranking of top cards)
 * - ReasonRater (rating of StrategyAnalyzer reasons)
 * - CoachRaterMini (rating of the coach LLM explanation)
 * - Submit button
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import CardRanker, { type CardRankingResult, type CardRankerHandle } from './CardRanker';
import ReasonRater, { type ReasonRatingResult, type SignalRatingResult } from './ReasonRater';
import CoachRaterMini, { type CoachRatingResult } from './CoachRaterMini';
import Card from './Card';
import type { CardNumber } from '../types/game';
import { substitutePlayerNames } from '../utils/cards';

// ============================================
// Types
// ============================================

export interface MLSuggestion {
  seat: number;
  card: number;
  card_name: string;
  confidence: number;
  alternatives?: Array<{ card: number; card_name: string; confidence: number }>;
  illegal_alternatives?: Array<{ card: number; card_name: string; confidence: number }>;
  entropy?: number;
  strategy?: Record<string, any>;
  explanation_fr?: string;
  all_probabilities?: Record<string, number>;
  legal_moves?: CardNumber[];
  model_slot?: string;
}

export interface FeedbackSubmitData {
  seat: number;
  trick_idx: number;
  position_in_trick: number;
  game_state: Record<string, unknown>;
  ml_recommended_card: number;
  ml_confidence?: number;
  ml_top5?: Array<{ card: number; card_name: string; confidence: number }>;
  ml_entropy?: number;
  ml_strategy?: Record<string, unknown>;
  ml_explanation?: string;
  expert_best_card?: number;
  expert_agrees?: boolean;
  expert_comment?: string;
  expert_confidence?: number;
  card_rankings?: CardRankingResult[];
  reason_ratings?: ReasonRatingResult[];
  signal_ratings?: SignalRatingResult[];
  coach_rating?: CoachRatingResult;
}

interface ExpertFeedbackPanelProps {
  mlSuggestion: MLSuggestion | null;
  feedbackSeat?: number;
  feedbackSeatName?: string;
  legalMoves: CardNumber[];
  hand: CardNumber[];
  coachAdvice?: string | null;
  trickIdx: number;
  positionInTrick: number;
  gameState: Record<string, unknown>;
  onSubmit: (data: FeedbackSubmitData) => void;
  cardClickRef?: React.MutableRefObject<((card: CardNumber) => void) | null>;
  taker?: number | null;
  viewPosition?: number;
}

// ============================================
// Entropy bar (reused from CoachChat)
// ============================================

function EntropyBar({ entropy }: { entropy: number }) {
  const maxEntropy = 4.0;
  const pct = Math.min(100, (entropy / maxEntropy) * 100);
  const label = entropy < 0.5 ? 'confiant' : entropy < 1.5 ? 'modéré' : entropy < 2.5 ? 'incertain' : 'très incertain';
  return (
    <span className="feedback-entropy-bar" title={`Entropie: ${entropy.toFixed(2)} (${label})`}>
      <span className="feedback-entropy-fill" style={{ width: `${pct}%` }} />
      <span className="feedback-entropy-label">{label} ({entropy.toFixed(1)})</span>
    </span>
  );
}

// ============================================
// ExpertFeedbackPanel component
// ============================================

const POSITION_LABELS: Record<number, string> = { 0: 'Sud', 1: 'Est', 2: 'Nord', 3: 'Ouest' };

export default function ExpertFeedbackPanel({
  mlSuggestion,
  feedbackSeat,
  feedbackSeatName,
  legalMoves,
  hand: _hand,
  coachAdvice,
  trickIdx,
  positionInTrick,
  gameState,
  onSubmit,
  cardClickRef,
  taker,
  viewPosition = 0,
}: ExpertFeedbackPanelProps) {
  const [cardRankings, setCardRankings] = useState<CardRankingResult[]>([]);
  const [reasonRatings, setReasonRatings] = useState<ReasonRatingResult[]>([]);
  const [signalRatings, setSignalRatings] = useState<SignalRatingResult[]>([]);
  const [coachRating, setCoachRating] = useState<CoachRatingResult | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [quickValidated, setQuickValidated] = useState(false);
  const lastSuggestionRef = useRef<number | null>(null);
  const cardRankerRef = useRef<CardRankerHandle>(null);
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const userInteractedRef = useRef(false);

  // Wire the card click ref so TrainingPage can forward hand clicks to the ranker
  useEffect(() => {
    if (cardClickRef) {
      cardClickRef.current = (card: CardNumber) => {
        cardRankerRef.current?.addOrToggleCard(card);
      };
      return () => { cardClickRef.current = null; };
    }
  }, [cardClickRef]);

  // Reset when suggestion changes
  if (mlSuggestion && mlSuggestion.card !== lastSuggestionRef.current) {
    lastSuggestionRef.current = mlSuggestion.card;
    setCardRankings([]);
    setReasonRatings([]);
    setSignalRatings([]);
    setCoachRating(null);
    setComment('');
    setSubmitted(false);
    setCountdown(null);
    setQuickValidated(false);
    userInteractedRef.current = false;
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }

  const doSubmit = useCallback((rankings: CardRankingResult[]) => {
    if (!mlSuggestion) return;

    const expertBest = rankings.length > 0 ? rankings[0].card_id : mlSuggestion.card;
    const expertAgrees = expertBest === mlSuggestion.card;

    const mlTop5 = mlSuggestion.alternatives
      ? [
          { card: mlSuggestion.card, card_name: mlSuggestion.card_name, confidence: mlSuggestion.confidence },
          ...mlSuggestion.alternatives,
        ]
      : [{ card: mlSuggestion.card, card_name: mlSuggestion.card_name, confidence: mlSuggestion.confidence }];

    onSubmit({
      seat: mlSuggestion.seat,
      trick_idx: trickIdx,
      position_in_trick: positionInTrick,
      game_state: gameState,
      ml_recommended_card: mlSuggestion.card,
      ml_confidence: mlSuggestion.confidence,
      ml_top5: mlTop5,
      ml_entropy: mlSuggestion.entropy,
      ml_strategy: mlSuggestion.strategy,
      ml_explanation: mlSuggestion.explanation_fr,
      expert_best_card: expertBest,
      expert_agrees: expertAgrees,
      expert_comment: comment || undefined,
      card_rankings: rankings.length > 0 ? rankings : undefined,
      reason_ratings: reasonRatings.length > 0 ? reasonRatings : undefined,
      signal_ratings: signalRatings.length > 0 ? signalRatings : undefined,
      coach_rating: coachRating ?? undefined,
    });

    setSubmitted(true);
    setCountdown(null);
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, [
    mlSuggestion, reasonRatings, signalRatings, coachRating,
    comment, trickIdx, positionInTrick, gameState, onSubmit,
  ]);

  const handleSubmit = useCallback(() => {
    if (!mlSuggestion || cardRankings.length === 0) return;
    doSubmit(cardRankings);
  }, [mlSuggestion, cardRankings, doSubmit]);

  // Countdown timer
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      doSubmit(cardRankings);
      return;
    }
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev === null) return null;
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    };
  }, [countdown, cardRankings, doSubmit]);

  const cancelCountdown = useCallback(() => {
    if (countdown !== null) {
      userInteractedRef.current = true;
      setCountdown(null);
      setQuickValidated(false);
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    }
  }, [countdown]);

  const handleQuickAccess = useCallback(() => {
    if (submitted) return;
    setQuickValidated(true);
    setTimeout(() => {
      submitBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    setCountdown(3);
    userInteractedRef.current = false;
  }, [submitted]);

  // Wrap evaluation callbacks to detect user interaction during countdown
  const handleCardRankingsChange = useCallback((rankings: CardRankingResult[]) => {
    setCardRankings(rankings);
    cancelCountdown();
  }, [cancelCountdown]);

  const handleReasonRatingsChange = useCallback((ratings: ReasonRatingResult[]) => {
    setReasonRatings(ratings);
    cancelCountdown();
  }, [cancelCountdown]);

  const handleSignalRatingsChange = useCallback((ratings: SignalRatingResult[]) => {
    setSignalRatings(ratings);
    cancelCountdown();
  }, [cancelCountdown]);

  const handleCoachRatingChange = useCallback((rating: CoachRatingResult | null) => {
    setCoachRating(rating);
    cancelCountdown();
  }, [cancelCountdown]);

  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setComment(e.target.value);
    cancelCountdown();
  }, [cancelCountdown]);

  if (!mlSuggestion) {
    return (
      <div className="expert-feedback-panel">
        <div className="expert-feedback-empty">
          En attente de la suggestion ML...
        </div>
      </div>
    );
  }

  const mlTop = mlSuggestion.alternatives
    ? [
        { card: mlSuggestion.card, card_name: mlSuggestion.card_name, confidence: mlSuggestion.confidence },
        ...mlSuggestion.alternatives,
      ]
    : [{ card: mlSuggestion.card, card_name: mlSuggestion.card_name, confidence: mlSuggestion.confidence }];

  const illegalAlts = mlSuggestion.illegal_alternatives ?? [];

  const strategy = mlSuggestion.strategy as {
    raison_principale?: { categorie: string; texte: string; importance?: number };
    raisons_secondaires?: Array<{ categorie: string; texte: string; importance?: number }>;
    risques?: string[];
    signaux_utilises?: string[];
    beliefs_utilises?: string[];
  } | null ?? null;

  const isCountingDown = countdown !== null && countdown > 0;

  return (
    <div className="expert-feedback-panel">
      {/* Whose turn indicator */}
      {feedbackSeat != null && (
        <div className="expert-feedback-seat-banner">
          Tour de <strong>{feedbackSeatName ?? POSITION_LABELS[feedbackSeat] ?? `P${feedbackSeat}`}</strong>
          {feedbackSeat !== 0 && (
            <span className="expert-feedback-seat-hint">
              ({POSITION_LABELS[feedbackSeat]}) — cliquez sur ses cartes
            </span>
          )}
        </div>
      )}

      {/* ML suggestion header with quick-access button */}
      <div className="expert-feedback-header">
        <div className="expert-feedback-suggestion">
          <div className="expert-feedback-reco-card">
            <Card card={mlSuggestion.card as CardNumber} size="small" />
          </div>
          <div className="expert-feedback-reco-info">
            <span className="expert-feedback-card-name">{mlSuggestion.card_name}</span>
            <span className="expert-feedback-confidence">
              {Math.round(mlSuggestion.confidence * 100)}%
            </span>
          </div>
          {!submitted && (
            <button
              className="expert-feedback-quick-btn"
              onClick={handleQuickAccess}
              title="Valider rapidement la carte recommandée"
            >
              ✓
            </button>
          )}
        </div>
        {mlSuggestion.entropy != null && (
          <EntropyBar entropy={mlSuggestion.entropy} />
        )}
      </div>

      {/* Explanation as tooltip on the card name */}
      {mlSuggestion.explanation_fr && (
        <div className="expert-feedback-explanation-tooltip-wrapper">
          <span className="expert-feedback-explanation-trigger">
            Explication ML
          </span>
          <div className="expert-feedback-explanation-tooltip">
            {substitutePlayerNames(mlSuggestion.explanation_fr, taker ?? null, feedbackSeat ?? 0, viewPosition)}
          </div>
        </div>
      )}

      {/* Card ranking (hidden during quick validation to keep eval sections visible) */}
      {!quickValidated && (
        <CardRanker
          ref={cardRankerRef}
          mlTop={mlTop}
          legalMoves={legalMoves}
          onChange={handleCardRankingsChange}
        />
      )}

      {/* Illegal ML suggestions */}
      {illegalAlts.length > 0 && (
        <div className="expert-feedback-illegal-section">
          <h5 className="card-ranker-title">
            Suggestions illégales du ML
          </h5>
          {illegalAlts.map((alt) => (
            <div key={alt.card} className="card-rank-item illegal-card-item">
              <span className="illegal-card-badge" title="Carte illégale">✕</span>
              <div className="card-rank-card">
                <Card card={alt.card as CardNumber} size="small" disabled />
              </div>
              <div className="card-rank-info">
                <span className="card-rank-ml-score">{Math.round(alt.confidence * 100)}%</span>
                <span className="illegal-card-label">illégale</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reason rating */}
      <ReasonRater
        strategy={strategy}
        onChange={handleReasonRatingsChange}
        onSignalChange={handleSignalRatingsChange}
        taker={taker}
        evaluatedSeat={feedbackSeat ?? 0}
        viewPosition={viewPosition}
      />

      {/* Coach rating */}
      <CoachRaterMini
        coachAdvice={coachAdvice ?? mlSuggestion.explanation_fr ?? null}
        onChange={handleCoachRatingChange}
      />

      {/* Comment */}
      <div className="expert-feedback-comment">
        <input
          type="text"
          placeholder="Commentaire libre (optionnel)..."
          value={comment}
          onChange={handleCommentChange}
        />
      </div>

      {/* Submit: plays the #1 ranked card (or ML reco if no ranking) */}
      <button
        ref={submitBtnRef}
        className={`expert-feedback-submit ${isCountingDown ? 'countdown-active' : ''}`}
        onClick={isCountingDown ? cancelCountdown : handleSubmit}
        disabled={submitted || (!isCountingDown && cardRankings.length === 0)}
      >
        {submitted
          ? '✓ Carte jouée'
          : isCountingDown
            ? `Envoi dans ${countdown}s — cliquer pour annuler`
            : cardRankings.length === 0
              ? 'Classez au moins une carte pour jouer'
              : 'Jouer la carte #1 et envoyer le feedback'}
      </button>
    </div>
  );
}
